import type { StateCreator } from 'zustand';
import { type AppStore, useAppStore } from '../index';
import { emptyHubBrand } from './hubBrandPersistence';

// gloss
const HUB_RESOLUTION_TIMEOUT_MS = 5000;

// gloss
export type HubResolution = { brandID: string | null; timedOut: boolean };

// gloss
const settledBrandID = (hub: HubBrandSlice['hubBrand']): string | null =>
  hub.isSwitching ? null : (hub.brandID ?? null);

// gloss
const whenResolved = (): Promise<HubResolution> => {
  const immediate = useAppStore.getState().hubBrand;
  const settled = settledBrandID(immediate);
  if (settled) return Promise.resolve({ brandID: settled, timedOut: false });
  if (typeof window === 'undefined' || (immediate.isResolved && !immediate.isSwitching)) {
    return Promise.resolve({ brandID: null, timedOut: false });
  }

  return new Promise((resolve) => {
    let done = false;
    const finish = (resolution: HubResolution) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(resolution);
    };

    const timer = setTimeout(() => finish({ brandID: null, timedOut: true }), HUB_RESOLUTION_TIMEOUT_MS);
    const unsubscribe = useAppStore.subscribe((state) => {
      const brandID = settledBrandID(state.hubBrand);
      if (brandID) finish({ brandID, timedOut: false });
      else if (state.hubBrand.isResolved && !state.hubBrand.isSwitching) finish({ brandID: null, timedOut: false });
    });

    const raced = settledBrandID(useAppStore.getState().hubBrand);
    if (raced) finish({ brandID: raced, timedOut: false });
  });
};

export type HubBrand = {
  uuid?: string;
  brandID?: string;
  code?: string;
  brandName?: string;
  hubLabel?: string;
};

// gloss
export type HubBrandSlice = {
  hubBrand: HubBrand & {
    isResolved: boolean;
    isSwitching: boolean;
    refusedUuid: string | undefined;
    refusedUuids: readonly string[];
    setHubBrand: (brand: HubBrand) => void;
    updateHubBrand: (brand: HubBrand) => void;
    whenResolved: () => Promise<HubResolution>;
    markSwitching: () => void;
    markResolved: () => void;
    resolveToNothing: () => void;
    refuse: (uuid: string | undefined) => void;
    clear: () => void;
  };
};

// gloss
const isRefusedPublish = (hub: HubBrandSlice['hubBrand'], brand: HubBrand): boolean =>
  brand.uuid ? hub.refusedUuids.includes(brand.uuid) : !hub.uuid && hub.refusedUuids.length > 0;

// gloss
export const createHubBrandSlice: StateCreator<AppStore, [['zustand/devtools', never]], [], HubBrandSlice> = (set) => ({
  hubBrand: {
    ...emptyHubBrand,
    isResolved: false,
    isSwitching: false,
    refusedUuid: undefined,
    refusedUuids: [],
    setHubBrand: (brand) =>
      set(
        (s) =>
          isRefusedPublish(s.hubBrand, brand)
            ? s
            : {
                hubBrand: {
                  ...s.hubBrand,
                  uuid: brand.uuid,
                  brandID: brand.brandID,
                  code: brand.code,
                  brandName: brand.brandName,
                  hubLabel: brand.hubLabel,
                  isResolved: true,
                  isSwitching: false,
                },
              },
        false,
        'hubBrand/set',
      ),
    updateHubBrand: (brand) =>
      set(
        (s) =>
          isRefusedPublish(s.hubBrand, brand)
            ? s
            : {
                hubBrand: {
                  ...s.hubBrand,
                  ...Object.fromEntries(Object.entries(brand).filter(([, value]) => value !== undefined)),
                },
              },
        false,
        'hubBrand/update',
      ),
    whenResolved,
    markSwitching: () =>
      set((s) => ({ hubBrand: { ...s.hubBrand, isResolved: false, isSwitching: true } }), false, 'hubBrand/switching'),
    markResolved: () =>
      set((s) => ({ hubBrand: { ...s.hubBrand, isResolved: true, isSwitching: false } }), false, 'hubBrand/resolved'),
    resolveToNothing: () =>
      set(
        (s) => ({ hubBrand: { ...s.hubBrand, ...emptyHubBrand, isResolved: true, isSwitching: false } }),
        false,
        'hubBrand/resolvedToNothing',
      ),
    refuse: (uuid) =>
      set(
        (s) => ({
          hubBrand: {
            ...s.hubBrand,
            ...emptyHubBrand,
            isResolved: false,
            isSwitching: true,
            refusedUuid: uuid,
            refusedUuids: uuid ? [...s.hubBrand.refusedUuids.filter((refused) => refused !== uuid), uuid] : [],
          },
        }),
        false,
        'hubBrand/refuse',
      ),
    clear: () =>
      set(
        (s) => ({
          hubBrand: {
            ...s.hubBrand,
            ...emptyHubBrand,
            isResolved: false,
            isSwitching: true,
            refusedUuid: undefined,
            refusedUuids: [],
          },
        }),
        false,
        'hubBrand/clear',
      ),
  },
});
